import React, { useEffect, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import OnsiteWaveformPlayer from './OnsiteWaveformPlayer';
import { Section, GOLD, RED } from './OnsiteFormPrimitives';

// Page 2 of the onsite form (IGFEMS-634): read-only summary that the participant and
// IGF staff check together. Data shown here is what Page 1 just saved.

const accommodationLabel = ({ staysAtHotel, staysAtOlympicVillage }) => {
  if (staysAtHotel && staysAtOlympicVillage) return 'Hotel and Olympic Village';
  if (staysAtHotel) return 'Hotel';
  if (staysAtOlympicVillage) return 'Olympic Village';
  return '—';
};

const Item = ({ label, value }) => (
  <div className="onsite-review__item">
    <dt className="onsite-review__label">{label}</dt>
    <dd className="onsite-review__value">{value || '—'}</dd>
  </div>
);

// Page 2 header, as in Wolfram's design (IGFEMS-634, Jira attachment 15191). Reuses the Page 1
// hero classes; the --staff modifier only swaps the colours and sizes.
const StaffHeroBanner = React.forwardRef(({ logo }, headingRef) => (
  <div className="onsite-form-hero onsite-form-hero--staff">
    <div className="onsite-form-hero__content">
      <div className="onsite-form-hero__top">
        <span className="onsite-form-hero__eyebrow">On-site Registration · Step 2 of 2</span>
      </div>
      <h2 ref={headingRef} tabIndex={-1} className="onsite-form-hero__welcome">Please see an IGF staff member</h2>
      <p className="onsite-form-hero__subtitle">They will check your details together with you.</p>
    </div>
    <div className="onsite-form-hero__logo-card">
      <img src={logo} alt="IGF — International Golf Federation" />
    </div>
  </div>
));

const OnsiteReviewPage = ({
  formData, label, isCaddie, presentCaddie, adminName, logo,
  busy, error, onBack, onValidate,
}) => {
  const headingRef = useRef(null);
  useEffect(() => { headingRef.current?.focus(); }, []);

  const mobilePhone = formData.registeredMobile ? `${formData.registeredMobileDial} ${formData.registeredMobile}`.trim() : '';
  const tournamentPhone = formData.mobileNumber ? `${formData.mobileDialCode} ${formData.mobileNumber}` : '';
  const caddieName = presentCaddie ? `${presentCaddie.name || ''} ${presentCaddie.lname || ''}`.trim() : '';

  return (
    <div className="onsite-form-page">
      <div className="onsite-form-body">
        <StaffHeroBanner ref={headingRef} logo={logo} />

        <Section icon="fa-user" title="Details" accent={GOLD}>
          <dl className="onsite-review__grid">
            <Item label="First Name" value={formData.firstName} />
            <Item label="Last Name" value={formData.lastName} />
            <Item label="Scoreboard Name" value={formData.scoreboardName} />
            <Item label="Height" value={formData.height ? `${formData.height} cm` : ''} />
            <Item label="Accommodation" value={accommodationLabel(formData)} />
            {formData.staysAtHotel && <Item label="Hotel Name" value={formData.onsiteHotelName} />}
            {!isCaddie && <Item label="Caddie Present Today" value={caddieName} />}
            <Item label="Mobile Number" value={mobilePhone} />
            <Item label="Number During Tournament" value={tournamentPhone} />
          </dl>
        </Section>

        <Section icon="fa-microphone" title="Name Pronunciation" accent={RED}>
          {formData.pronunciationAudioUrl
            ? <OnsiteWaveformPlayer src={formData.pronunciationAudioUrl} />
            : <p className="onsite-review__empty">No recording.</p>}
        </Section>

        <Section icon="fa-pen-nib" title="Signature" accent={GOLD}>
          <div className="onsite-review__signature">
            {formData.signatureDataUrl
              ? <img src={formData.signatureDataUrl} alt="Participant signature" />
              : <span className="onsite-review__empty">No signature.</span>}
            <span className="onsite-review__lock"><i className="fa-solid fa-lock" aria-hidden="true" /> Locked</span>
          </div>
          {formData.regulationsAcknowledged && (
            <p className="onsite-review__regs">
              <i className="fa-solid fa-circle-check" aria-hidden="true" /> Agreed to the IGF regulations.
            </p>
          )}
        </Section>

        <section className="onsite-review__staff" aria-label="IGF staff validation">
          <div className="onsite-review__staff-label">IGF staff validation</div>
          <div className="onsite-review__validator" title="Set from the logged-in admin account. Cannot be changed.">
            <i className="fa-solid fa-lock" aria-hidden="true" />
            <span>Validated by: <strong>{adminName || 'Logged-in admin'}</strong></span>
            <small>Logged-in admin · not editable</small>
          </div>

          {error && (
            <div className="onsite-form-error" role="alert">
              <i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {error}
            </div>
          )}

          <div className="onsite-review__actions">
            <button type="button" className="onsite-review__back" onClick={onBack} disabled={busy}>
              <i className="fa-solid fa-arrow-left" aria-hidden="true" /> Back to edit
            </button>
            <button type="button" className="onsite-form-footer__btn onsite-form-footer__btn--ready" onClick={onValidate} disabled={busy}>
              {busy
                ? <><Spinner animation="border" size="sm" /> Saving…</>
                : <><i className="fa-solid fa-circle-check" aria-hidden="true" /> Validate {label} Registration</>}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default OnsiteReviewPage;
